import { useEffect, useState } from "react";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Globe,
  ArrowLeft,
  CheckCircle2,
  Shield,
  RefreshCw,
  AlertTriangle,
  Settings,
} from "lucide-react";
import {
  api,
  Domain,
  DkimSelector,
} from "@/lib/api";
import { useAuthStore } from "@/stores/auth";
import { toast } from "@/lib/toast";
import { EnforcementTab } from "@/components/domain/EnforcementTab";
import { ConfigurationTab } from "@/components/domain/ConfigurationTab";
import { SpineSourcesView } from "@/pages/sources";
import { ShieldCheck } from "lucide-react";

import { useFeatureDiscovery } from "@/lib/ux-analytics";
// Phase 1 PR 16 IA collapse: the per-domain tab strip is now
// Overview / Sources / Configuration. Previously the first tab was
// labelled "Enforcement"; the content didn't change but the framing
// did — the spine-driven /enforcement page owns the blast-radius
// simulator, and this tab is the natural per-domain landing.
type TabType = "overview" | "sources" | "configuration";

// Map old / aliased tab IDs to current ones. `?tab=enforcement`
// (PR 8 - PR 15 era) and the older legacy aliases all redirect to
// the canonical Overview tab.
const TAB_REDIRECTS: Record<string, TabType> = {
  enforcement: "overview",
  dkim: "configuration",
  dns: "configuration",
  bimi: "configuration",
  reports: "overview",
  tasks: "overview",
};

export function DomainDetail() {
  useFeatureDiscovery("domain_detail");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { organization } = useAuthStore();
  const [domain, setDomain] = useState<Domain | null>(null);
  const [dkimSelectors, setDkimSelectors] = useState<DkimSelector[]>([]);

  // Get initial tab from URL query param, default to enforcement
  const tabFromUrl = searchParams.get("tab");
  const validTabs: TabType[] = ["overview", "sources", "configuration"];
  const resolvedTab: TabType =
    tabFromUrl && validTabs.includes(tabFromUrl as TabType)
      ? (tabFromUrl as TabType)
      : tabFromUrl && TAB_REDIRECTS[tabFromUrl]
        ? TAB_REDIRECTS[tabFromUrl]
        : "overview";
  const [activeTab, setActiveTab] = useState<TabType>(resolvedTab);
  const [loading, setLoading] = useState(true);

  // Sync activeTab when URL search params change (e.g. browser back/forward)
  useEffect(() => {
    const currentTab = searchParams.get("tab");
    const resolved: TabType =
      currentTab && validTabs.includes(currentTab as TabType)
        ? (currentTab as TabType)
        : currentTab && TAB_REDIRECTS[currentTab]
          ? TAB_REDIRECTS[currentTab]
          : "overview";
    setActiveTab(resolved);
    // Fix up legacy tab aliases in the URL
    if (currentTab && TAB_REDIRECTS[currentTab]) {
      setSearchParams({ tab: resolved }, { replace: true });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        const result = await api.getDomainDetails(id!);
        if (cancelled) return;
        setDomain(result.domain);
        // Phase 1 PR 16.1: per-domain sources now load via the spine
        // /api/sources endpoint embedded in the Sources tab; the
        // legacy `result.sources` is no longer consumed here.
        setDkimSelectors(result.dkimSelectors);
      } catch (error) {
        if (!cancelled) console.error("Failed to fetch domain:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [id]);

  async function fetchDomainData() {
    try {
      setLoading(true);
      const result = await api.getDomainDetails(id!);
      setDomain(result.domain);
      // result.sources intentionally unused; per-domain sources now
      // load via the spine /api/sources call inside SpineSourcesView.
      setDkimSelectors(result.dkimSelectors);
    } catch (error) {
      console.error("Failed to fetch domain:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerifyDomain() {
    if (!id) return;
    try {
      await api.verifyDomain(id);
      fetchDomainData();
    } catch (error) {
      console.error("Failed to verify domain:", error);
      toast.error("Failed to verify domain");
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="text-center py-12">
        <Globe className="w-16 h-16 text-slate-300 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-slate-800 mb-2">
          Domain not found
        </h2>
        <p className="text-slate-500 mb-4">
          The domain you're looking for doesn't exist.
        </p>
        <Link
          to="/domains"
          className="text-amber-600 hover:text-amber-700 font-medium"
        >
          Back to Domains
        </Link>
      </div>
    );
  }

  const tier = organization?.billing_tier || "free";

  const tabs: { id: TabType; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { id: "overview", label: "Overview", icon: ShieldCheck },
    { id: "sources", label: "Sources", icon: Shield },
    { id: "configuration", label: "Configuration", icon: Settings },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/domains")}
          className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-800">{domain.domain_name}</h1>
            {domain.verification_status === "verified" ? (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                <CheckCircle2 className="w-4 h-4" />
                Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                <AlertTriangle className="w-4 h-4" />
                Pending Verification
              </span>
            )}
          </div>
          <p className="text-slate-500">
            Added {new Date(domain.created_at).toLocaleDateString()}
          </p>
        </div>
        {domain.verification_status !== "verified" && (
          <button
            onClick={handleVerifyDomain}
            className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-medium hover:bg-amber-400 transition-colors"
          >
            <RefreshCw className="w-5 h-5" />
            Verify Domain
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 -mx-4 px-4 md:mx-0 md:px-0">
        <nav className="flex gap-4 lg:gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchParams({ tab: tab.id }, { replace: true });
              }}
              className={`flex items-center gap-2 px-3 py-3 border-b-2 transition-colors whitespace-nowrap text-sm ${
                activeTab === tab.id
                  ? "border-amber-500 text-amber-600"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <tab.icon className="w-4 h-4 flex-shrink-0" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <EnforcementTab domainId={domain.id} domainName={domain.domain_name} tier={tier} />
      )}
      {/* Phase 1 PR 16.1: per-domain Sources tab now embeds the spine
          view scoped to this domain — same 3-bucket layout the
          standalone /sources page uses. Eliminates the pre-PR-16.1
          duplicate that read from the legacy sending_sources table. */}
      {activeTab === "sources" && <SpineSourcesView lockedDomainId={id} />}
      {activeTab === "configuration" && (
        <ConfigurationTab
          domain={domain}
          dkimSelectors={dkimSelectors}
          domainId={domain.id}
          tier={tier}
          onRefresh={fetchDomainData}
        />
      )}
    </div>
  );
}
